import Employee from '@/employee/Employee'
import util from '@/util'
import ValueRange, { MapStringAny } from '@/effects/ValueRange'
import { reactive, toRaw } from 'vue'
import Laboratory from '@/farm/Laboratory'
import globalState from '@/store/globalState'
import Research from '../Research'
import { deserialize, serialize } from 'serializr'
import CollectionPlayer from '@/employee/CollectionPlayer'
import ValueMod from '@/effects/ValueMod'
import EffectManager from '@/effects/EffectManager'
import EventManager from '@/EventManager'
import EffectType from "@/effects/EffectType"
import FarmEvent from "@/FarmEvent"
import Office from "@/farm/Office"
import { Personality } from "@/employee/Personality"
import { allPersonalities } from "@/employee/Personalities"
import Market from "@/farm/Market"

export default class Farm {
  state = reactive({
    employees: [] as Employee[],
    staffLimit: new ValueRange(2, 1, 6), // Default value defined in load()
    milk: new ValueRange(0, 0, 2),
    milkPrice: new ValueRange(40, 30, 100, [], true),
    milkingSpeed: new ValueRange(0.1, 0), // Default value defined in load()
    milkSold: 0,
    money: 300,
    time: 0,
    timeDilationFactor: 0.5,
    lab: new Laboratory(),
    market: new Market(),
    researchSpending: 0,
    office: new Office(),
    effectManager: new EffectManager(),
    eventManager: new EventManager(),
    saveName: 'Default Save',
    saveDate: Date.now(),
    gameVersion: Farm.GAME_VERSION
  })

  public manuallyPaused: boolean = false
  private lastUpdate: number = 0
  private _updateIsRunning: boolean = false
  private updateTimer: any = 0
  private static readonly GAME_VERSION = '1.1.1'

  get effectToModsMap() {
    return {
      milkStorage: this.state.milk.maxMods,
      milkPrice: this.state.market.basePrice.valueMods,
      milkConsumption: this.state.market.consumption.valueMods,
      milkingSpeed: this.state.milkingSpeed.valueMods,
      staffLimit: this.state.staffLimit.valueMods
    }
  }

  constructor() {
    this.state.lab = new Laboratory(JSON.parse(globalState().researchTreeString.value), this)
    this.state.office = new Office(this)
  }

  load(state: any) {
    this.state.gameVersion = Farm.GAME_VERSION

    this.state.lab = new Laboratory(JSON.parse(globalState().researchTreeString.value), this)
    this.state.lab.load(deserialize(Laboratory, state.lab))

    this.state.office = new Office(this)
    this.state.office.load(deserialize(Office, state.office))

    // @ts-ignore
    this.state.staffLimit.load(deserialize(ValueRange, (state.staffLimit) ? state.staffLimit : new ValueRange(2, 1, 10)))
    this.state.employees = []
    for (let employee of state.employees) {
      // @ts-ignore
      let d: Employee = deserialize(Employee, employee)
      let newEmployee = new Employee(this)
      newEmployee.load(d)
      this.state.employees.push(newEmployee)
    }

    this.state.milkSold = state.milkSold
    this.state.money = state.money
    // @ts-ignore
    this.state.milk.load(deserialize(ValueRange, state.milk))
    // @ts-ignore
    this.state.milkPrice.load(deserialize(ValueRange, state.milkPrice))
    // @ts-ignore
    this.state.milkingSpeed.load(deserialize(ValueRange, (state.milkingSpeed) ? state.milkingSpeed : new ValueRange(0.1, 0)))
    this.state.time = state.time
    this.state.timeDilationFactor = (state.timeDilationFactor) ? state.timeDilationFactor : 1
    this.state.researchSpending = state.researchSpending
    this.state.effectManager = deserialize(EffectManager, state.effectManager)
    this.state.eventManager = deserialize(EventManager, state.eventManager)
    this.state.market = new Market(this)
    this.state.market.load((state.market) ? deserialize(Market, state.market) : new Market(this))
    this.state.saveName = state.saveName

    this.state.effectManager.refreshEffects(this.state.lab as Laboratory, this.effectToModsMap)

    //console.log("Loaded", this.state)
  }

  update() {
    // Calculate dt
    let dt = (performance.now() - this.lastUpdate) / 1000
    this.lastUpdate = performance.now()

    dt = dt * this.state.timeDilationFactor

    this.state.time += dt

    // Apply effects to farm from all unlocked researches
    for (let research of this.state.lab.availableForFarm()) {
      this.state.effectManager.addResearch(research, this.effectToModsMap)
    }
    this.state.effectManager.update(dt, this.effectToModsMap)

    this.state.eventManager.update(dt, this)
    this.state.market.update(dt)
    this.state.milkPrice.maxMods["market"] = new ValueMod(this.state.market.maxPrice - 100, 'add')

    this.updateEmployees(dt)
    this.state.lab.update(dt, this.getEmployeesAssignedTo('lab'))

    // Office
    this.state.office.update(dt)
  }

  updateEmployees(dt: number) {
    for (let employee of this.state.employees) {
      // Salary
      let salary = employee.salary.value * dt
      employee.pay(salary)
      this.state.money -= salary

      // Mood
      let salaryFulfillment = (salary / (employee.targetSalary * dt)) - 1
      employee.mood.valueMods.salary = new ValueMod(employee.constants["salaryMood"] * salaryFulfillment, 'add')

      if (this.state.money < 0) {
        employee.effectManager.addTempEffect('out-of-money', 'mood',
          new ValueMod(0, 'mul'),
          new EffectType(120, 'slow')
        )
        employee.say('outOfMoney', 'outOfMoney', 60)
      } else {
        employee.effectManager.removeTempEffect('out-of-money', 'mood')
      }

      // Milk employee if we should
      if (employee.isBeingMilked) {
        this.milk(dt, employee as Employee)
        employee.chatHistory.addMessage(employee.getSpeech("milking"), { id: "milking", maxFrequency: 22 })
      }
    }

    this.state.employees.map(e => e.update(dt, this))
  }

  milk(dt: number, employee: Employee, options: any = {}) {
    let useWorker = "worker" in options
    let speedFactor = (useWorker) ? options.worker.milkingSpeed : 1
    let amount = -this.state.milkingSpeed.value * dt * speedFactor
    let actualAmount = Math.abs(employee.milk.addValue(amount))
    this.state.milk.baseValue += actualAmount

    // If we are getting milk as fast as we are milking, add lust
    // That is, no extra lust if we always hold milk button, or a worker is milking
    if (actualAmount > (this.state.milkingSpeed.value * 0.9) * dt && !useWorker) {
      employee.lust.addValue(4 * employee.lustDelta.value * dt)
    }
  }

  toggleUpdating() {
    if (this._updateIsRunning) {
      this.manuallyPaused = true
      this.stopUpdating()
    } else {
      this.manuallyPaused = false
      this.startUpdating()
    }
  }

  startUpdating() {
    if (this.updateIsRunning || this.manuallyPaused) return

    this.lastUpdate = performance.now()
    this._updateIsRunning = true
    this.updateTimer = setInterval(() => this.update(), 100)
  }

  stopUpdating() {
    if (!this.updateIsRunning) return

    this._updateIsRunning = false
    clearInterval(this.updateTimer)
  }

  get milkSellValue(): number {
    return this.state.milk.value * this.milkprice
  }

  get milkprice(): number {
    let price = this.state.milkPrice.value

    return price
  }

  sell() {
    let milkAmount = this.state.milk.value
    if (milkAmount < 0.05) return

    let data = this.state.market.sell(milkAmount, this.state.milkPrice.value)
    let cost = data.money
    let sold = data.sold
    this.state.milk.baseValue = milkAmount - sold
    this.state.money += cost
    this.state.milkSold += cost

    this.state.eventManager.addEvent(new FarmEvent((args: any) => {
      return `We sold ${util.showNumber(sold)} L milk for $${Math.round(cost)}.`
    }), this)
  }

  research(research: Research) {
    let cost = this.state.lab.getResearchCost(research)
    if (this.state.money >= cost || research.isStarted) {
      if (!research.isStarted) {
        this.state.money -= cost
        this.state.researchSpending += cost
      }
      this.state.lab.startResearch(research)
    }
  }

  /** Assigns an employee to a workstation.
   * @param employee
   * @param word One of: office, farm, lab
   */
  assignWork(employee: Employee, word: string) {
    employee.assignment = word
  }

  startMilking(employee: Employee) {
    employee.isBeingMilked = true
  }

  stopMilking(employee: Employee) {
    employee.isBeingMilked = false
  }

  get giftPrice(): number {
    return 20
  }

  gift(employee: Employee) {
    //console.log("Effect overview")
    //employee.effectManager.overview()

    if (this.state.money > this.giftPrice) {
      this.state.money -= this.giftPrice
      employee.gift()
    }
  }

  fuck(employee: Employee) {
    employee.fuck()
  }

  cuddle(employee: Employee) {
    employee.cuddle()
  }

  setAssignment(e: Employee, assignment: string) {
    e.assignment = assignment
  }

  getEmployeesAssignedTo(assignment: string): Employee[] {
    let workers: Employee[] = []
    for (let e of this.state.employees) {
      if (e.assignment === assignment) {
        workers.push(e as Employee)
      }
    }
    return workers
  }

  get currentSalaries(): number {
    let sum = this.state.employees.map((e) => e.salary.value).reduce((acc, val) => acc + val, 0)
    return sum
  }

  get lifetimeSalaries(): number {
    let sum = this.state.employees.map((e) => e.money).reduce((acc, val) => acc + val, 0)
    return sum
  }

  get production(): number {
    let sum = this.state.employees.map((e) => e.milkDelta.value).reduce((acc, val) => acc + val, 0)
    return sum
  }

  get productionValue(): number {
    return this.production * this.milkprice
  }

  get researchSpending(): number {
    return this.state.researchSpending
  }

  get employeeLimitString(): string {
    let employees = this.state.employees.length
    let limit = this.state.staffLimit
    if (employees < limit.value) {
      return `${employees} / ${limit.value}`
    } else if (employees < limit.max) {
      return "Research Required"
    }

    return `Max Staff Reached (${this.state.staffLimit.max})`
  }

  get canHire(): boolean {
    return this.state.money >= this.hiringCost() && this.state.employees.length < this.state.staffLimit.value
  }

  hiringCost(): number {
    let costs = [50, 100, 200, 500, 1000]
    let employees = this.state.employees.length
    if (employees < costs.length) return costs[employees]
    return costs[costs.length - 1]
  }

  hire() {
    if (!this.canHire) return
    this.state.money -= this.hiringCost()

    let employee = new Employee(this)
    // Add all previously unlocked researches to new inventory
    for (let research of this.state.lab.availableForEmployees()) {
      employee.inventory.addResearch(research)
    }
    this.state.employees.push(employee)
  }

  employeeById(uuid: string) {
    for (let e of this.state.employees) {
      if (e.uuid === uuid) return e
    }
    return undefined
  }

  get updateIsRunning(): boolean {
    return this._updateIsRunning
  }

  notifyResearchCompletion(research: Research) {
    if (research.target == 'employee') {
      for (let e of this.state.employees) {
        e.inventory.addResearch(research)
      }
    }

    if (research.id == 'victory-1') {
      this.state.eventManager.addEvent(new FarmEvent((args: any) => {
        return `Global hypnosis broadcast activated. The world is ours! We've won!`
      }), this)
    }
  }

  pay(amount: number) {
    //console.log(amount)
    this.state.money -= amount
  }

  canPay(amount: number): boolean {
    return amount <= this.state.money
  }
}
